import { z } from 'zod';
import { CalendarDateSchema } from './common.js';
import { nullableText } from '../sanitised-text.js';
// =============================================================================
// Document Payment Domain
//
// Schemas for the payment domain. Distinct from the subscription billing
// payment types in billing.ts which cover Paystack subscription payments.
//
// This body schema records a single payment against ONE document and is reused
// by both the IN (client invoice) and OUT (supplier invoice / expense) routes;
// both post a settlement journal on ledgered accounts. Multi-document allocation,
// credit-note targets, and the method/origin enums are added with their later
// phases (see the roadmap comments in payment.prisma).
// =============================================================================
export const DocumentPaymentStatusSchema = z.enum(['RECORDED', 'VOIDED']);
export const PaymentDirectionSchema = z.enum(['IN', 'OUT']);
// Explicit discriminant for which funding source funded a payment's
// settlement leg (the intent layer over the pre-existing ref columns -
// bankAccountId, fundingLedgerAccountId, fundingCreditNoteId). Mirrors the
// Prisma `PaymentFundingSource` enum. CLIENT_CREDIT and SUPPLIER_ON_ACCOUNT are
// the two ref-less members (an operator-applied client overpayment / supplier
// on-account remainder - no matching ref column).
export const PaymentFundingSourceSchema = z.enum([
    'BANK_ACCOUNT',
    'LEDGER_ACCOUNT',
    'CREDIT_NOTE',
    'CLIENT_CREDIT',
    'SUPPLIER_ON_ACCOUNT',
]);
const BankAccountFundingMemberSchema = z.object({
    source: z.literal('bankAccount'),
    // The bank account the money landed in. Selects the per-bank Payments
    // Clearing account the settlement journal debits (bank-funded,
    // reconcilable). Ownership is verified at the route against the path account.
    bankAccountId: z.string().uuid('Bank account ID must be a valid UUID'),
});
const LedgerAccountFundingMemberSchema = z.object({
    source: z.literal('ledgerAccount'),
    // A GL account the settlement is funded from directly (any non-clearing
    // GL account - a loan, contra, or rounding account, never a clearing
    // account) - a GL-funded settlement that never touches Payments Clearing.
    // Ownership is verified at the route against the path account.
    fundingLedgerAccountId: z.string().uuid('Funding ledger account ID must be a valid UUID'),
});
const CreditNoteFundingMemberSchema = z.object({
    source: z.literal('creditNote'),
    // An existing OPEN credit note (issued, posted journal, remaining balance
    // > 0) belonging to the invoice's client - applies it as a "later
    // application" (the client's credit note, originally issued against a
    // different invoice or not yet applied, settling THIS invoice). Fetch
    // the client's open credit notes via
    // GET /accounts/:accountId/clients/:clientId/available-credit. Ownership
    // is verified at the route against the path account.
    fundingCreditNoteId: z.string().uuid('Funding credit note ID must be a valid UUID'),
});
const ClientCreditFundingMemberSchema = z.object({
    // Applies the client's document-free unallocated overpayment remainder
    // (money already paid in but not yet applied to any invoice or credit
    // note). No reference field - there is nothing to select beyond the
    // client itself. Check
    // GET /accounts/:accountId/clients/:clientId/available-credit's
    // `unallocatedRemainderMinorUnits` first to know how much is available.
    source: z.literal('clientCredit'),
});
const SupplierOnAccountFundingMemberSchema = z.object({
    // Applies the supplier's document-free unallocated on-account remainder
    // (money already paid to the supplier but not yet applied to a supplier
    // invoice/expense - minted by a money-out bank reconcile). No reference
    // field - there is nothing to select beyond the supplier itself. OUT-only
    // (the inverse of `clientCredit`/`creditNote`'s IN-only note above). Check
    // GET /accounts/:accountId/suppliers/:supplierId/on-account-balance's
    // `unallocatedRemainderMinorUnits` first to know how much is available.
    source: z.literal('supplierOnAccount'),
});
// Which funding source funds a MONEY-IN (client invoice) payment's settlement
// leg, plus the matching reference. A discriminated union (not sibling
// optional fields + a .refine one-of) so "exactly one source" is structural,
// not enforced by validation. `creditNote` and `clientCredit` are the two
// client-side credit-application members; `bankAccount` and `ledgerAccount`
// are shared with the money-out schema below.
export const MoneyInFundingSourceSchema = z.discriminatedUnion('source', [
    BankAccountFundingMemberSchema,
    LedgerAccountFundingMemberSchema,
    CreditNoteFundingMemberSchema,
    ClientCreditFundingMemberSchema,
]);
// Which funding source funds a MONEY-OUT (supplier invoice / expense)
// payment's settlement leg. `supplierOnAccount` is the payable-side mirror of
// `clientCredit` above; a supplier invoice payment never applies a client
// credit note, so this union has no `creditNote` member.
export const MoneyOutFundingSourceSchema = z.discriminatedUnion('source', [
    BankAccountFundingMemberSchema,
    LedgerAccountFundingMemberSchema,
    SupplierOnAccountFundingMemberSchema,
]);
// The combined five-member union, used only by consumers that genuinely serve
// both directions at once: the route-layer ownership-check helpers
// (`funding-envelope.ts`, shared by the invoice and supplier-invoice routes),
// the `FundingSourcePicker` UI (one component rendering both directions), and
// the CLI's shared payment-plan builder. A route's own request body schema
// must use the direction-scoped union above, never this one, so a chat-exposed
// money-in tool can never accept `supplierOnAccount`.
export const FundingSourceSchema = z.discriminatedUnion('source', [
    BankAccountFundingMemberSchema,
    LedgerAccountFundingMemberSchema,
    CreditNoteFundingMemberSchema,
    ClientCreditFundingMemberSchema,
    SupplierOnAccountFundingMemberSchema,
]);
// One of the client's open (ISSUED, posted-journal) credit notes with a
// remaining balance, as returned by the available-credit read below - the
// "later application" source for a `{ source: 'creditNote' }` funding member.
// A trimmed, single-client projection of `MatchableCreditNoteSchema`
// (bank-statement.ts): `remainingMinorUnits` is the same figure that schema
// calls `outstandingMinorUnits` (this domain's own vocabulary for "how much of
// this credit note is left to apply").
export const AvailableCreditNoteSchema = z.object({
    creditNoteId: z.string().uuid(),
    creditNoteNumber: z.string().nullable(),
    /** The invoice this credit note was originally issued against. */
    invoiceNumber: z.string().nullable(),
    remainingMinorUnits: z.number().int(),
    currency: z.string(),
    /** ISO 8601 calendar date (YYYY-MM-DD) of the credit note. */
    date: z.string(),
});
// Response for GET /accounts/:accountId/clients/:clientId/available-credit -
// the two credit sources an invoice payment can apply as `funding`: the
// client's open credit notes (each with remaining > 0) and the client's
// document-free unallocated overpayment remainder. Feeds the apply-credit
// source picker.
export const AvailableCreditResponseSchema = z.object({
    creditNotes: z.array(AvailableCreditNoteSchema),
    unallocatedRemainderMinorUnits: z.number().int(),
    currency: z.string(),
});
// Response for GET /accounts/:accountId/suppliers/:supplierId/on-account-balance
// - the payable-side twin of AvailableCreditResponseSchema above.
// A supplier has no credit-note pool on this path (supplier credit notes are a
// separate mechanism), so this is deliberately a smaller shape than the client
// response rather than a reuse of it: forcing a permanently-empty
// `creditNotes: []` into the wire contract would be a lie the frontend then has
// to treat as meaningful. The client's document-free unallocated overpayment
// remainder and the supplier's document-free on-account remainder are the same
// *concept* but not the same *response contract*.
export const SupplierOnAccountBalanceResponseSchema = z.object({
    unallocatedRemainderMinorUnits: z.number().int(),
    currency: z.string(),
});
// Fields shared by the money-in and money-out record-payment bodies below,
// minus `funding` - each direction narrows that field to its own union so the
// wire contract (and the chat-exposed money-in tool's schema) never admits the
// other direction's ref-less credit member.
const DocumentPaymentBodyFieldsSchema = z.object({
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
    amountMinorUnits: z.number().int().min(1, 'Payment amount must be greater than zero'),
    // Real-world value date of the payment (date-only, no time-of-day).
    paidAt: CalendarDateSchema,
    reference: nullableText(255, 'Reference must be 255 characters or less'),
    notes: nullableText(2000, 'Notes must be 2000 characters or less'),
});
// Request body for POST /accounts/:accountId/invoices/:invoiceId/payments.
// The invoice (and therefore the client) is identified by the path, so the
// body carries only the payment's own attributes. The frontend pre-fills
// amountMinorUnits with the invoice's current outstanding balance (default
// full payment); a smaller amount records a partial payment.
export const RecordInvoicePaymentBodySchema = DocumentPaymentBodyFieldsSchema.extend({
    // Which source funds the settlement leg and its matching reference. A
    // discriminatedUnion cannot sit beside sibling top-level fields, so it
    // lives under this envelope key.
    funding: MoneyInFundingSourceSchema,
});
// Request body for POST /accounts/:accountId/supplier-invoices/:supplierInvoiceId/payments.
// The money-out twin of RecordInvoicePaymentBodySchema above - same shared
// fields, narrowed to the money-out funding union.
export const RecordSupplierInvoicePaymentBodySchema = DocumentPaymentBodyFieldsSchema.extend({
    funding: MoneyOutFundingSourceSchema,
});
// Request body for the super-admin settlement-journal repair
// (POST /accounts/:accountId/invoices/:invoiceId/payments/:paymentId/settlement-journal).
// Posts the missing settlement journal for a historic journal-less payment.
// The payment and its anchor invoice are identified by the path; the body
// carries the funding bank account (resolved to its per-bank Payments Clearing
// account, exactly as a normal record would) and an optional amount assertion
// so a hand-driven repair fails loud on a mismatched target.
export const RepairPaymentSettlementBodySchema = z.object({
    bankAccountId: z.string().uuid('Bank account ID must be a valid UUID'),
    expectedAmountMinorUnits: z
        .number()
        .int()
        .min(1, 'Expected amount must be greater than zero')
        .optional(),
});
// Request body for the super-admin settlement-journal resplit
// (POST /accounts/:accountId/invoices/:invoiceId/payments/:paymentId/resplit-settlement-journal
//  and its supplier-invoice twin). Reverses a historic multi-allocation
// payment's untagged LUMP settlement journal and reposts it with one
// document-tagged control leg per allocation (era-4 lump residue - see
// domains/ledger/README.md § Settlement backfill). The payment and its anchor document
// are identified by the path; the amount assertion is REQUIRED (unlike the
// repair sibling) because a resplit reverses a posted journal - a hand-driven
// incident call must prove it is aimed at the intended payment before anything
// is written.
export const ResplitPaymentSettlementBodySchema = z.object({
    expectedAmountMinorUnits: z.number().int().min(1, 'Expected amount must be greater than zero'),
});
// =============================================================================
// Payment - E2E Bypass
// =============================================================================
export const E2EAccountPaymentsParamsSchema = z.object({
    accountId: z.uuid(),
});
export const E2EAccountPaymentsResponseSchema = z.object({
    payments: z.array(z.object({
        id: z.uuid(),
        status: DocumentPaymentStatusSchema,
        ledgerEntrySetId: z.uuid().nullable(),
        createdAt: z.string(),
    })),
});
